import {liveTextStyles, liveTextVisibleStyles} from '../../_utils'

const globalStyles = {
    'html, body': {
        fontFamily: "'GTEesti', sans-serif",
        fontWeight: 300,
        textRendering: 'geometricPrecision',
        scrollBehavior: 'smooth',
    },
    // Companion to `viewport-fit=cover` (set in the viewport meta in _app). `cover` makes the layout
    // extend edge-to-edge behind the notch, home indicator and side insets, which stabilizes the iOS
    // viewport while the Liquid Glass toolbar collapses on scroll (removing the swiper→footer jitter).
    // This wrapper restores the insets `cover` removed, so no content is hidden under the system UI.
    // The top inset is intentionally omitted: the app already offsets content below the fixed header
    // via the header-size padding, and the fixed header itself covers the top notch area.
    '.safe-area-wrapper': {
        paddingRight: 'env(safe-area-inset-right, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        paddingLeft: 'env(safe-area-inset-left, 0px)',
    },
    // iOS 26 Safari renders its bottom "Liquid Glass" toolbar as a translucent surface and samples
    // the page content behind it to tint the bar, so scrolling content shows through and flickers
    // (home / PLP / PDP). This thin shim is pushed almost fully off-screen (only ~5px remains, itself
    // behind the toolbar) and painted with a solid, near-opaque color. Safari samples it as the bar's
    // backdrop and renders the bar solid instead of showing the content. Being only a few pixels tall
    // and off the visible area, it never covers real content (swiper, sticky CTA).
    //
    // Double-gated so it appears ONLY where it is actually needed:
    //  - `@supports (-webkit-touch-callout: none)` → WebKit/Safari only (inert on Chrome/Firefox/desktop);
    //  - `.has-dynamic-bottom-bar` → set by _app only when a collapsing translucent bar is detected
    //    (100lvh ≠ 100svh). On Safari WITHOUT the Liquid Glass bar (e.g. iPhone X / iOS 16, whose
    //    toolbar is opaque) the class is absent, so the shim's ~5px white strip never shows above the
    //    classic bar. `pointer-events: none` keeps it from intercepting taps.
    //    The element is rendered in _app as `<Box className="safari-bar-shim" />`.
    '@supports (-webkit-touch-callout: none)': {
        '.has-dynamic-bottom-bar .safari-bar-shim': {
            position: 'fixed',
            left: 0,
            bottom: '-5px',
            width: '100%',
            height: '10px',
            backgroundColor: '#FFFFFF',
            zIndex: 99999,
            pointerEvents: 'none',
            // Forces Safari to treat the element as an opaque layer it must sample for the bar tint.
            opacity: 0.99,
        },
    },
    // Firefox for Android: same translucent dynamic bottom bar / backdrop-sampling problem as iOS 26
    // Safari, fixed with the SAME shim. Scoped to Firefox via `@supports (-moz-appearance: none)` and
    // gated by `.has-dynamic-bottom-bar-firefox` (set in _app only when Firefox + a collapsing bar are
    // detected). This branch is completely disjoint from the WebKit/Safari one above — `-moz-appearance`
    // is Firefox-only, so Safari never matches it and the iOS handling is left untouched.
    '@supports (-moz-appearance: none)': {
        '.has-dynamic-bottom-bar-firefox .safari-bar-shim': {
            position: 'fixed',
            left: 0,
            bottom: '-5px',
            width: '100%',
            height: '10px',
            backgroundColor: '#FFFFFF',
            zIndex: 99999,
            pointerEvents: 'none',
            opacity: 0.99,
        },
    },
    '.no-focus-outline *:focus, [data-js-focus-visible] :focus:not([data-focus-visible-added]):not( [data-focus-visible-disabled] )': {
        outlineOffset: '0 !important',
        outlineColor: 'transparent !important',
        outlineStyle: 'none !important',
        outlineWidth: '0 !important',
        boxShadow: 'none !important',
    },
    '.no-focus-outline *:focus-within': {
        outlineOffset: '0 !important',
        outlineColor: 'transparent !important',
        outlineStyle: 'none !important',
        outlineWidth: '0 !important',
        boxShadow: 'none !important',
        // borderColor: 'inherit !important',
    },
    '*:focus': {
        outlineOffset: '3px !important',
        outlineColor: '#1e90ff !important',
        outlineStyle: 'auto !important',
        outlineWidth: '4px !important',
        boxShadow: 'none !important',
    },
    'body a:hover, body .chakra-link:hover': {
        textDecoration: 'none',
    },
    'body .chakra-modal__content-container': {
        zIndex: 'tooltip',
    },
    // Elevates Popper.js wrapper only when the PDP zoom modal is open.
    // .chakra-popover__popper is injected at body level with inline z-index by Popper.js;
    // !important is required to override the inline style. Must stay above the elevated PDP
    // stickybar (1900) below, otherwise the stickybar intercepts clicks meant to close it.
    // EXCLUDE the mobile confirmation-layer popper (`.cc-confirm-layer-popover`): its Popover ships its
    // own manual tap-outside overlay at z-index 149999 and relies on its content sitting at 150000 (above
    // that overlay) so its buttons are tappable. That popper is portalled into the zoom modal footer, so
    // it is NOT a descendant of `.cc-mobile` and would otherwise be matched here and demoted to 1902 —
    // below its own overlay — which swallowed the "Add to cart"/PayPal taps and closed the layer.
    'body:has(.cc-zoom-modal) :not(.cc-mobile) .chakra-popover__popper:not(:has(.cc-confirm-layer-popover))': {
        zIndex: '1902 !important',
    },
    // Elevates PDP stickybar above zoom modal only when zoom is open.
    'body:has(.cc-zoom-modal) [data-testid="pdp-stickybar"]': {
        zIndex: 1900,
        background: '#FFFFFFE5',
    },
    // Any other drawer/modal (e.g. minicart) opened on top of the zoom modal must stay above the
    // stickybar's temporary elevation above, otherwise it would render underneath it.
    // (the container itself never carries the custom className, only its inner dialog child does)
    'body:has(.cc-zoom-modal) .chakra-modal__content-container:not(:has(.cc-zoom-modal))': {
        zIndex: '1901 !important',
    },
    // Elevates the share dark overlay and mobile share popup above the zoom modal (whose thumbnail
    // strip and buy button otherwise sit above them) so sharing still works while the zoom is open.
    // Must also stay above the elevated PDP stickybar (1900), otherwise it stays clickable on top of
    // the overlay and the ShareBox can't be dismissed (e.g. on tablet).
    'body:has(.cc-zoom-modal) [data-testid="dark-overlay"]': {
        zIndex: '1901 !important',
    },
    'body:has(.cc-zoom-modal) [data-testid="share-box-mobile"]': {
        zIndex: '1903 !important',
    },
    'body #chakra-toast-manager-top-right': {
        top: {
            base: '100px !important',
            md: '189px !important',
        },
        left: {
            base: '16px !important',
            md: 'auto !important',
        },
        right: {
            base: '16px !important',
            md: '50px !important',
        },
    },
    '.grecaptcha-badge': {
        display: 'none',
    },
    // Salesforce Embedded Chat Styles
    '.embeddedServiceHelpButton .helpButton': {
        width: 'auto !important',
        bottom: '16px',
        right: '16px',
        height: '65px',
    },
    '.embeddedServiceHelpButton .helpButtonDisabled': {
        display: 'none !important',
        visibility: 'hidden',
    },
    '.embeddedServiceHelpButton .helpButton .uiButton': {
        backgroundColor: 'transparent',
        fontFamily: "'GTEesti', sans-serif",
        fontWeight: 300,
        borderRadius: 0,
        minWidth: '1px',
        maxWidth: 'none',
        padding: '0px',
        letterSpacing: '3px',
        textTransform: 'uppercase',
        fontSize: '12px',
        boxShadow: 'none',
        transition: 'all .15s ease-in',
        height: '65px',
        backgroundRepeat: 'no-repeat',
    },
    '.embeddedServiceHelpButton .helpButton .uiButton:hover': {
        color: '#262626',
    },
    '.embeddedServiceHelpButton .helpButton .uiButton:hover::before, .embeddedServiceHelpButton .helpButton .uiButton:focus::before': {
        display: 'none',
    },
    'button[embeddedservice-chatheader_chatheader] svg': {
        maxWidth: '19px',
    },
    '.embeddedServiceHelpButton .uiButton .helpButtonLabel': {
        display: 'none',
    },

    // Animations to be injected separately
    '@global': `
  
      @keyframes changePosition {
        0% { transform: translate(15px, 0); }
        40.1% { transform: translate(0, 0); }
        60.1% { transform: translate(0, 0); }
        100% { transform: translate(-15px, 0); }
      }
  
      @-webkit-keyframes webChangePosition {
        0% { -webkit-transform: translate(15px, 0); }
        40.1% { -webkit-transform: translate(0, 0); }
        60.1% { -webkit-transform: translate(0, 0); }
        100% { -webkit-transform: translate(-15px, 0); }
      }
  
      @keyframes changeOpacity {
        0% { opacity: 0; }
        40.1% { opacity: 1; }
        60.1% { opacity: 1; }
        100% { opacity: 0; }
      }
  
      @-webkit-keyframes webChangeOpacity {
        0% { opacity: 0; }
        40.1% { opacity: 1; }
        60.1% { opacity: 1; }
        100% { opacity: 0; }
      }
  
      .embeddedServiceHelpButton .embeddedServiceIcon {
        margin-right: 0px;
        width: 65px;
        display: inline-block !important;
        animation: changeIcon 4000ms infinite, changeOpacity 2000ms infinite, changePosition 2000ms linear infinite;
        -webkit-animation: changeIcon 4000ms infinite, webChangeOpacity 2000ms infinite, webChangePosition 2000ms linear infinite;
      }
    `,
    '.embedded-messaging > .embeddedMessagingFrame[class~="isMaximized"]': {
        zIndex: 9999,
    },
    // The Salesforce embedded-messaging markup is `#embedded-messaging >
    // .embeddedMessagingConversationButtonWrapper > button#embeddedMessagingConversationButton`. The
    // WRAPPER is the fixed element that actually occupies the on-screen slot, and the Salesforce script
    // sets a dynamic `bottom` on it (observed `bottom: <innerHeight>px`, which pins it to the TOP of the
    // viewport). Positioning only the button is not enough — the visible bubble follows the wrapper. So
    // we anchor BOTH the wrapper and the button to the bottom via the controller's offset, and use
    // `!important` on the wrapper to win over the script's inline/dynamic value.
    '.embeddedMessagingConversationButtonWrapper': {
        bottom: 'calc(20px + var(--cc-chat-bottom-offset, 0px)) !important',
        top: 'auto !important',
        // Soft animation as the anchor moves (e.g. band → CTA on PDP).
        transition: 'bottom 0.3s ease !important',
    },
    'button#embeddedMessagingConversationButton': {
        // The vertical offset is driven by the `useStickyChatPosition` controller via the
        // `--cc-chat-bottom-offset` CSS custom property set on `document.documentElement`, so the chat
        // bubble always sits above the active sticky section (class `cc-sticky-section`)
        // on desktop and mobile. Defaults to 0px → base 20px when no sticky section is active.
        bottom: 'calc(20px + var(--cc-chat-bottom-offset, 0px))',
        right: '16px',
        pointerEvents: 'all !important',
        // Soft animation as the anchor moves (band → CTA, or down to base on route change).
        transition: 'bottom 0.3s ease',
    },
    '.paypal_container': {
        width: '100%',
    },
    '.sr-only': {
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: '0',
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        whiteSpace: 'nowrap',
        border: '0',
    },
    // Accessibility: WCAG 1.4.5 – Live text for images containing burned-in text.
    // Default: visually hidden but present in DOM (selectable, zoomable, screen-reader accessible).
    // When html.un-assistive is active: rendered visibly below the image.
    '.cc-livetext': liveTextStyles,
    '.un-assistive .cc-livetext': liveTextVisibleStyles,
}

export default globalStyles
